import React, { useEffect } from 'react';

import {
    TACHES_UTILISATEUR_SLICE_NAME,
    useUtilisateurService,
    UtilisateurTaches,
    utilisateurTachesSelector,
} from '@europrocurement/l2d-domain';
import {
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import { StoreDatatable } from '@europrocurement/flexy-datatable';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { useDispatch } from 'react-redux';
import { useTasksTableHook } from '@europrocurement/l2d-modules';
import { REALM_RESP_FORMALIST, UseKeycloakService } from '@europrocurement/l2d-keycloak';
import useTasksHook from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import { addTaskIcon } from '@europrocurement/l2d-icons';
import { useSnackbar } from 'notistack';
import { type AppDispatch, useAppSelector } from '../../../../redux/types';

/**
 * Page de listing des taches liées aux dossiers de formalités.
 */
const ListeTachesUtilisateur: React.FunctionComponent = function () {
    const dispatch = useDispatch<AppDispatch>();
    const datasource = useAppSelector(utilisateurTachesSelector);
    const { getUtilisateurTaches, utilisateurApi } = useUtilisateurService();
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { getColumnsTachesUtilisateur, getFiltersTachesUtilisateur } = useTasksTableHook({
        apiTarget: 'utilisateur',
    });
    const { handleAddTask, handleEditTask } = useTasksHook({ apiTarget: 'utilisateur' });
    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const isFormalistManager = keycloakService.checkRole(REALM_RESP_FORMALIST);
    const { enqueueSnackbar } = useSnackbar();

    // Chargement initial des données
    // Filtre les tâches de l'utilisateur connecté ou affiche l'ensemble des tâches pour un manager
    useEffect(() => {
        if (isFormalistManager) {
            dispatch(getUtilisateurTaches({}));
        } else if (userIdentifier) {
            utilisateurApi.apiUtilisateursGetCollection({ userIdentifier }).then((response) => {
                const user = response.data['hydra:member'][0] ?? null;

                if (user && user.userIdentifier === userIdentifier) {
                    dispatch({
                        type: `${TACHES_UTILISATEUR_SLICE_NAME}/setmainFilter`,
                        payload: {
                            key: 'tacheAssignTo',
                            value: user['@id'],
                        },
                    });

                    // Displayed label on formalist filter
                    if (user.prenom && user.nom) {
                        dispatch({
                            type: `${TACHES_UTILISATEUR_SLICE_NAME}/setmainFilterLabel`,
                            payload: { key: 'tacheAssignTo', label: `${user.prenom} ${user.nom}` },
                        });
                    }
                    dispatch(getUtilisateurTaches({}));
                } else {
                    enqueueSnackbar('Utilisateur non trouvé - identifiant non conforme', {
                        variant: 'error',
                    });
                }
            });
        } else {
            enqueueSnackbar('Utilisateur non trouvé', { variant: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdentifier]);

    const onClickRow = (e: React.MouseEvent, task: UtilisateurTaches) => {
        handleEditTask(task);
    };

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des données, échec du chargement des tâches."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={6}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={[isFormalistManager ? 'Liste des tâches' : 'Liste de mes tâches']}
                actions={
                    <HeaderActionButton
                        label="Ajouter une tâche"
                        icon={addTaskIcon}
                        onClick={handleAddTask}
                        actionType="navigation"
                    />
                }
            />
            {datasource && (
                <StoreDatatable
                    dataSource={datasource}
                    columns={getColumnsTachesUtilisateur()}
                    fetchData={getUtilisateurTaches}
                    filters={getFiltersTachesUtilisateur()}
                    localStorageKey="FlexyStoreDatatable"
                    onClickRow={onClickRow}
                    observers={[]}
                />
            )}
        </SkeletonLoader>
    );
};

export default ListeTachesUtilisateur;
